import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';
import { unreadBadge } from '@/lib/unread-badge';

// Avisos sin leer de la empresa activa (so=370): contador del menú, de la campana y de
// la bandeja. Una sola consulta compartida (misma clave), refrescada cada minuto y al
// volver a la pestaña del navegador. La empresa va en la posición 1 del queryKey.

const countSchema = z.object({
  success: z.literal(true),
  data: z.object({ unread: z.number(), pendingAction: z.number() }),
});

export function useUnreadCount({
  companyId,
  enabled,
}: {
  companyId: string | null;
  enabled: boolean;
}): UseQueryResult<number> {
  const api = useApi();
  return useQuery({
    queryKey: ['notifications-count', companyId],
    queryFn: async () =>
      countSchema.parse(await api.get<unknown>('/api/v1/me/notifications/count')).data.unread,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export interface UnreadBadge {
  count: number;
  /** Texto del contador; null sin avisos sin leer (o mientras carga). */
  label: string | null;
  /** No se pudo consultar y no hay un número anterior: se dice, no se muestra como cero
   *  (UX-ERR-01). Si falla una recarga se conserva el último número. */
  unknown: boolean;
}

/** Cantidad y texto del contador de avisos sin leer de la empresa activa. */
export function useUnreadBadge(): UnreadBadge {
  const { activeCompanyId } = useActiveCompany();
  const q = useUnreadCount({ companyId: activeCompanyId, enabled: Boolean(activeCompanyId) });
  const count = q.data ?? 0;
  return { count, label: unreadBadge(count), unknown: q.isError && q.data === undefined };
}
